/**
 * E-POSTEN SOM ER SENDT (jobb 2, 0491) — det en liste viser om meldingen bak en invitasjon eller
 * en lenke: status, adressen den gikk til, og når statusen sist endret seg.
 *
 * Bevisst uten `.meta({ id })`: den er en del av radene den står i (`PendingInvitation`, `Share`,
 * Control Rooms invitasjoner), ikke et svar på egen hånd. `cancelled` er ikke med — en trukket
 * melding vises aldri; lista viser den siste som ikke er trukket.
 *
 * `not_allowed` (jobb 3, 0495) er unntaket: en melding mottakerlista i testmiljøet stoppet
 * (`recipient_not_allowed`). Den vises som «Ikke sendt (testmiljø)», så ingen venter på den.
 *
 * `not_sent` (260.16): «sendt» av den falske e-postdriveren (`provider = 'fake'`), altså lokalt og i
 * testene. Ingenting gikk ut, og Control Room › Drift sier «Sender ikke (fake)». Også den vises som
 * «Ikke sendt (testmiljø)».
 */
import { z } from 'zod';

export const MESSAGE_DELIVERY_STATUSES = [
  'queued',
  'sent',
  'delivered',
  'bounced',
  'blocked',
  'failed',
  'not_allowed',
  'not_sent',
] as const;
export type MessageDeliveryStatus = (typeof MESSAGE_DELIVERY_STATUSES)[number];
export const messageDeliveryStatusSchema = z.enum(MESSAGE_DELIVERY_STATUSES);

export const messageDeliverySchema = z.object({
  status: messageDeliveryStatusSchema,
  recipient: z.string(),
  statusAt: z.iso.datetime(),
});
export type MessageDelivery = z.infer<typeof messageDeliverySchema>;

/** Statusen en side viser: Venter, Sendt, Levert, Avvist, og «Ikke sendt (testmiljø)» av to grunner. */
export type MessageDeliveryTone =
  'waiting' | 'sent' | 'delivered' | 'rejected' | 'notAllowed' | 'notSent';
export function messageDeliveryTone(status: MessageDeliveryStatus): MessageDeliveryTone {
  switch (status) {
    case 'queued':
      return 'waiting';
    case 'sent':
      return 'sent';
    case 'delivered':
      return 'delivered';
    case 'not_allowed':
      return 'notAllowed';
    case 'not_sent':
      return 'notSent';
    default:
      return 'rejected';
  }
}
/** Gjør en rad fra basen til kontrakten. Ukjent eller manglende status er «ingen e-post». */
export function toMessageDelivery(
  status: string | null | undefined,
  recipient: string | null | undefined,
  statusAt: string | Date | null | undefined,
): MessageDelivery | null {
  const parsed = messageDeliveryStatusSchema.safeParse(status);
  if (!parsed.success || recipient == null || statusAt == null) return null;
  const at = statusAt instanceof Date ? statusAt : new Date(statusAt);
  if (Number.isNaN(at.getTime())) return null;
  return { status: parsed.data, recipient, statusAt: at.toISOString() };
}

/**
 * Hva svaret på en handling vet om e-posten den la i køen (sak 7 og 44, minstevarianten):
 * `queued` til en tillatt adresse, `not_allowed` når mottakerlista i testmiljøet stopper den, eller
 * `not_sent` når e-postdriveren ikke sender (`fake`, lokalt — 260.16).
 * Kvitteringen sier «sendt» bare for `queued`; ellers «Ikke sendt … Kopier lenken nå».
 */
export const EMAIL_DELIVERY_OUTCOMES = ['queued', 'not_allowed', 'not_sent'] as const;
export const emailDeliveryOutcomeSchema = z.enum(EMAIL_DELIVERY_OUTCOMES);
export type EmailDeliveryOutcome = z.infer<typeof emailDeliveryOutcomeSchema>;

/* ------------------------------------------------------------------------- SMS (jobb 3, 0495) */

/**
 * Telefonnummeret slik Brevo vil ha det: E.164 uten «+», som `4791234567` — eller null når det ikke
 * er et mobilnummer vi tør sende til. Ett svar for nettappen (skal SMS tilbys?) og API-et (hvor går
 * den?), så de aldri er uenige.
 *
 * Et norsk nummer uten landkode er et mobilnummer bare når det er åtte siffer og begynner på 4 eller
 * 9. Med landkode (`+` eller `00`) godtas 8–15 siffer; for +47 gjelder den samme mobilregelen.
 */
export function smsNumber(phone: string | null | undefined): string | null {
  if (phone == null) return null;
  const compact = phone.trim().replace(/[\s().-]/g, '');
  let digits: string;
  if (compact.startsWith('+')) digits = compact.slice(1);
  else if (compact.startsWith('00')) digits = compact.slice(2);
  else if (/^[49]\d{7}$/.test(compact)) digits = '47' + compact;
  else return null;
  if (!/^[1-9]\d{7,14}$/.test(digits)) return null;
  if (digits.startsWith('47') && !/^47[49]\d{7}$/.test(digits)) return null;
  return digits;
}

/**
 * `GET /v1/messaging/channels`: hva nettappen kan tilby. SMS bare når plattformbryteren er på.
 * `emailRedirectTo`: i testmiljøet går all e-post til denne ene innboksen (`MESSAGING_REDIRECT_EMAIL_TO`);
 * null i drift. Adressen er ingen hemmelighet.
 */
export const messagingChannelsResponseSchema = z
  .object({ sms: z.boolean(), emailRedirectTo: z.string().nullable().default(null) })
  .meta({ id: 'MessagingChannelsResponse' });
export type MessagingChannelsResponse = z.infer<typeof messagingChannelsResponseSchema>;

/** Sendte SMS i én måned (`2026-10`), i Oslo-tid. Bare tallet. */
export const smsUsageMonthSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  sent: z.number().int().nonnegative(),
});
export type SmsUsageMonth = z.infer<typeof smsUsageMonthSchema>;

/** `GET /v1/platform/organizations/:id/sms-usage`: de siste tolv månedene, nyeste først. */
export const smsUsageResponseSchema = z
  .object({ items: z.array(smsUsageMonthSchema) })
  .meta({ id: 'SmsUsageResponse' });
export type SmsUsageResponse = z.infer<typeof smsUsageResponseSchema>;

export const SMS_DRIVERS = ['disabled', 'fake', 'brevo'] as const;
export type SmsDriver = (typeof SMS_DRIVERS)[number];

/** `GET`/`PUT /v1/platform/messaging/sms`: bryteren, driveren serveren kjører med, og telleren. */
export const platformSmsSettingResponseSchema = z
  .object({
    enabled: z.boolean(),
    updatedAt: z.iso.datetime(),
    driver: z.enum(SMS_DRIVERS),
    /** Hele plattformen, de siste tolv månedene, nyeste først. */
    usage: z.array(smsUsageMonthSchema),
  })
  .meta({ id: 'PlatformSmsSettingResponse' });
export type PlatformSmsSettingResponse = z.infer<typeof platformSmsSettingResponseSchema>;

export const updatePlatformSmsSettingRequestSchema = z
  .strictObject({ enabled: z.boolean() })
  .meta({ id: 'UpdatePlatformSmsSettingRequest' });
export type UpdatePlatformSmsSettingRequest = z.infer<typeof updatePlatformSmsSettingRequestSchema>;
